import { colors } from '@mathflat/common/style'
import clsx from 'clsx'
import { observer } from 'mobx-react'
import type { MouseEvent } from 'react'
import { useEffect, useRef, useState } from 'react'
import { Scrollbars } from 'react-custom-scrollbars-2'
import Divider from 'src/component/atoms/Divider'
import { Icon } from 'src/component/atoms/Icon'
import type { IconType } from 'src/component/atoms/Icon/Icon'
import { DrawerTemplate } from 'src/component/templates/DrawerTemplate'
import type { NotificationCase, NotificationCTAOption, NotificationState } from 'src/dto'

import useInfiniteScroll from '@/hooks/useInfiniteScroll'
import useRepository from '@/hooks/useRepository'
import useScrollbars from '@/hooks/useScrollbars'
import { LessonGroupService } from '@/page/Lesson/_service'
import { AchievementAnalysisService } from '@/page/Lesson/AchievementAnalysis/_service/AchievementAnalysis.service.ts'
import ReportListService from '@/page/Lesson/Report/ReportList/ReportList.service'
import commonRepo from '@/repository/common.repo'
import {
  RouteName,
  수능_콘텐츠_업데이트_경로,
  시중교재전체지원_콘텐츠업데이트_경로,
} from '@/router'
import StudentGroupAccordionService from '@/service/StudentGroupAccordion.service'
import WebviewService from '@/service/webview.service'
import appPostMsg from '@/utils/appPostMsg'
import { mathflatUtils } from '@/utils/mathflatUtils'
import { mixpanelClient } from '@/utils/trackings/mixpanel'

import 새알림없음 from '../../emptyList/새알림없음'
import NotificationService from './Notification.service'
import S from './Notification.style'
import NotificicationItem from './NotificationItem'
import { releaseNotificationUtils } from './notificationUtils'

const Notification = () => {
  const ref = useRef<HTMLDivElement | null>(null)
  const service = useRepository(NotificationService)
  const { scrollToTop, el } = useScrollbars()
  const [showPopup, setShowPopup] = useState<boolean>(false)
  const popupTimeout = useRef<number | null>(null)
  useInfiniteScroll(ref, () => service.fetchMoreNotifications())

  const handleClose = () => {
    scrollToTop()
    service.close()
  }
  const handleClickTop = (e) => {
    e.preventDefault()
    scrollToTop()
    service.clearNewNotiCountSinceOpen()
    setShowPopup(false)
  }

  useEffect(() => {
    if (!commonRepo.academy || commonRepo.isSchoolflat) return

    releaseNotificationUtils
      .initNotificationData('ESSAY_WORKSHEET_OPEN_NOTIFICATION', new Date('2025-12-31'))
      .then((res) => {
        if (!res) return

        const { startDate, expiredDate, isRead } = res
        service.essayWorksheetNotification = releaseNotificationUtils.createEssayNotification({
          startDate,
          expiredDate,
          isRead,
        })
      })

    releaseNotificationUtils
      .initNotificationData('PUBLIC_WORKBOOK_OPEN_NOTIFICATION', new Date('2025-11-26'))
      .then((res) => {
        if (!res) return

        const { startDate, expiredDate, isRead } = res
        service.publicWorkbookSupportedNotification =
          releaseNotificationUtils.createPublicWorkbookOpenNotification({
            startDate,
            expiredDate,
            isRead,
          })
      })
  }, [commonRepo.academy, commonRepo.isSchoolflat, service])

  useEffect(() => {
    if (service.newNotiCountSinceOpen > 0 && el && el.clientHeight < el.scrollHeight) {
      setShowPopup(true)

      popupTimeout.current = window.setTimeout(() => {
        setShowPopup(false)
      }, 3000)
    }
  }, [service.newNotiCountSinceOpen])

  useEffect(() => {
    return () => {
      popupTimeout.current && window.clearTimeout(popupTimeout.current)
    }
  }, [])
  return (
    <DrawerTemplate isOpen={service.isOpen} onClose={handleClose} css={S.Template}>
      <div className={clsx('new-noti-popup', showPopup && 'pop')}>
        <div>
          <span>
            새로운 알림 <span>{service.newNotiCountSinceOpen}건</span>이 있어요
          </span>
          <button onClick={handleClickTop}>맨 위로</button>
        </div>
      </div>
      <S.Header>
        <button onClick={handleClose} className="close-button">
          <Icon name="close" color={colors.gray.$900} size={28} />
        </button>
        <div className="title">
          <strong>알림센터</strong>
          {!service.isEmptyUnexpiredNotifications && (
            <S.ReadAllButton onClick={async () => await service.readAll()}>
              <Icon name="check" color={colors.gray.$600} /> 전체 읽음
            </S.ReadAllButton>
          )}
        </div>
      </S.Header>
      <Scrollbars className="table-scroll-body">
        <S.Section>
          {service.isEmptyUnexpiredNotifications ? (
            <새알림없음 />
          ) : (
            <>
              {service.todayNotifications.map(
                ({
                  sortKey,
                  isRead,
                  message,
                  title,
                  formatToNotificationDatetime,
                  state,
                  id,
                  case2,
                  ctas,
                  payload,
                }) => {
                  const title1 = payload.groupName ?? ''
                  const title2 = title ?? ''
                  return (
                    <NotificicationItem
                      key={sortKey}
                      title1={title1}
                      title2={title2}
                      ctaOptions={GetCtaOptions(case2, sortKey, ctas, id, payload)}
                      iconOption={getIconOption(state, case2)}
                      {...{ isRead, message, formatToNotificationDatetime }}
                    />
                  )
                },
              )}
              {service.pastNotifications.length > 0 && (
                <div
                  className={clsx(
                    'horizontal-divider',
                    service.todayNotifications.length === 0 && 'no-data-today',
                  )}
                >
                  이전 알림 <Divider type="horizontal" size={313} color={colors.gray.$400} />
                </div>
              )}
              {service.pastNotifications.map(
                ({
                  sortKey,
                  isRead,
                  message,
                  title,
                  formatToNotificationDatetime,
                  state,
                  id,
                  payload,
                  case2,
                  ctas,
                }) => {
                  const title1 = payload.groupName ?? ''
                  const title2 = title ?? ''

                  return (
                    <NotificicationItem
                      key={sortKey}
                      title1={title1}
                      title2={title2}
                      ctaOptions={GetCtaOptions(case2, sortKey, ctas, id, payload)}
                      iconOption={getIconOption(state, case2)}
                      {...{ isRead, message, formatToNotificationDatetime }}
                    />
                  )
                },
              )}
            </>
          )}
          <div ref={ref} />
        </S.Section>
      </Scrollbars>
    </DrawerTemplate>
  )
}

const getIconOption = (
  state: NotificationState,
  case2: NotificationCase,
): { name: IconType; color?: string; size?: number } => {
  switch (state) {
    case 'FAILURE': {
      return {
        name: 'failure',
        color: colors.red.$300,
        size: 24,
      }
    }
  }

  switch (case2) {
    case 'EMAIL': {
      return {
        name: 'message',
        size: 24,
      }
    }
    case 'GROUP_REPORT_CREATE':
    case 'GROUP_REPORT_UPDATE':
    case 'ONE_CLICK_REPORT':
    case 'ONE_CLICK_REPORT_CAS':
    case 'ACHIEVEMENT_REPORT_CREATE':
    default: {
      return {
        name: 'report',
        size: 24,
      }
    }
  }
}

// TODO: use 훅쓰려면 감싸는 함수는 첫문자 대문자 rule때문에 대문자로 그침
// 여기 타입이랑.. 한번 리팩토링해야할듯
const GetCtaOptions = (case2, sortKey, ctas, id, payload): NotificationCTAOption[] => {
  const service = useRepository(NotificationService)
  const lessonGroupService = useRepository(LessonGroupService)
  const reportListService = useRepository(ReportListService)
  const studentGroupAccordionService = useRepository(
    [StudentGroupAccordionService, RouteName.lesson],
    RouteName.lesson,
  )
  const achievementAnalysisService = useRepository(AchievementAnalysisService)

  const func: (cta: notification.CTA) => NotificationCTAOption = ({ action, text }) => {
    switch (action) {
      case 'NONE': {
        return {}
      }
      case 'READ': {
        return {
          onCTAClick: async (e: MouseEvent) => {
            service.handleItemClick(sortKey)
            if (
              (payload.eventType === 'WEAK_WORKSHEET_BY_WEAK_CONCEPT_CHIP' ||
                payload.eventType === 'WEAK_WORKSHEET' ||
                payload.eventType === 'GROUP_WEAK_WORKSHEET_BY_WEAK_CONCEPT_CHIP_CREATE') &&
              payload.groupKey &&
              payload.groupType
            ) {
              const url = mathflatUtils.generateWeakWorksheetPath(
                payload.groupType,
                payload.groupKey,
              )
              lessonGroupService.setLessonLastPath(url)
              const newGroupInfo = await LessonGroupService.getUrlGroupInfoFromPath(url)
              studentGroupAccordionService.setSelectedGroupInfo(newGroupInfo)
              newGroupInfo && lessonGroupService.groupLastPath.set(newGroupInfo.key, url) // 해당하는 그룹에 마지막 경로 저장

              commonRepo.router.push(url)
              service.close()
              return
            }

            if (
              payload.type === 'MATHFLAT_SINGLE_REPORT_PREVIEW' &&
              payload.groupKey &&
              payload.groupType
            ) {
              const url = mathflatUtils.generateAchievementAnalysisPath(
                payload.groupType,
                payload.groupKey,
              )
              lessonGroupService.setLessonLastPath(url)
              const newGroupInfo = await LessonGroupService.getUrlGroupInfoFromPath(url)
              studentGroupAccordionService.setSelectedGroupInfo(newGroupInfo)
              newGroupInfo && lessonGroupService.groupLastPath.set(newGroupInfo.key, url) // 해당하는 그룹에 마지막 경로 저장

              commonRepo.router.push(url)
              service.close()
              return
            }

            if (payload.groupKey && payload.groupType) {
              // TODO: Lesson 쪽 라우팅 시 이런 식의 세팅이 많이 들어가는데, 라우팅 관련 리팩토링 필요.
              const url = mathflatUtils.generateReportPath(payload.groupType, payload.groupKey)

              lessonGroupService.setLessonLastPath(url)
              const newGroupInfo = await LessonGroupService.getUrlGroupInfoFromPath(url)
              studentGroupAccordionService.setSelectedGroupInfo(newGroupInfo)
              newGroupInfo && lessonGroupService.groupLastPath.set(newGroupInfo.key, url) // 해당하는 그룹에 마지막 경로 저장

              commonRepo.router.push(url)
              service.close()
            }
          },
        }
      }
      case 'FILE_DOWNLOAD': {
        return {
          text,
          onCTAClick: (e: MouseEvent) => {
            service.deleteCTA({ sortKey, action: 'FILE_DOWNLOAD' })
          },
        }
      }
      case 'FILE_DOWNLOAD_API': {
        return {
          text,
          onCTAClick: (e: MouseEvent) => {
            if (case2 === 'ONE_CLICK_REPORT_CAS') {
              reportListService.downloadOrExportCas(id)
            } else {
              reportListService.downloadOrExport(id)
            }
          },
        }
      }
      case 'FILE_DOWNLOAD_ID_LIST': {
        return {
          text: WebviewService.isAppAndroid ? '내보내기' : text,
          onCTAClick: (e: MouseEvent) => {
            e.stopPropagation()
            if (WebviewService.isAppAndroid) {
              appPostMsg.exportReport({ reportIds: payload.ids, groupName: payload.groupName })
            } else {
              reportListService.download(payload.ids, payload.groupName)
            }
            service.deleteCTA({ sortKey, action: 'FILE_DOWNLOAD_API' })
          },
        }
      }
      case 'PREVIEW': {
        return {
          text,
          onCTAClick: (e: MouseEvent) => {
            e.stopPropagation()
            achievementAnalysisService.preview(payload.id)
          },
        }
      }
      case 'CLIENT_NOTIFICATION_PUBLIC_WORKBOOK_OPEN': {
        return {
          text,
          onCTAClick: (e: MouseEvent) => {
            e.stopPropagation()
            mixpanelClient.track('시중교재전체지원_진입점_알림')
            service.handlePublicWorkbookNotificationItemClick(sortKey)
            commonRepo.router.push(시중교재전체지원_콘텐츠업데이트_경로)
            service.close()
          },
        }
      }
      case 'CLIENT_NOTIFICATION_ESSAY_WORKSHEET_OPEN': {
        return {
          text,
          onCTAClick: (e: MouseEvent) => {
            e.stopPropagation()
            mixpanelClient.track('서술형_진입점_알림')
            service.handleEssayNotificationItemClick(sortKey)
            commonRepo.router.push(수능_콘텐츠_업데이트_경로)
            service.close()
          },
        }
      }
    }
  }

  return ctas.map(func)
}

export default observer(Notification)
